import React, { useEffect, useRef, useState } from "react";

// hooks
import { usePauseOtherMedia } from "../../../../hooks/usePauseOtherMedia";
import { IMediaItem } from "../../../../interfaces/content/content";



const UseContentCarousel = ({mediaItems, token, videoAutoPlayEnabled}: {mediaItems: IMediaItem[], token?: string, videoAutoPlayEnabled: boolean}) => {
    const SLIDE_DURATION = 6000;
    const FADE_DURATION = 0.3;
    const [counter, setCounter] = useState<number>(0);
    const [isPaused, setIsPaused] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    
    //this ensures we aren't building and destroying videos with each carousel cycle
    const [loadedVideoUrls, setLoadedVideoUrls] = useState<Set<string>>(() => new Set());
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const activeVideoRef = useRef<HTMLVideoElement | null>(null);
    const pauseOtherMedia = usePauseOtherMedia(activeVideoRef);

    //if we pause the carousel, and later play we need to know how much longer to play it for
    const timerRef = useRef<number | null>(null);
    const timerStartedAtRef = useRef<number | null>(null);
    const remainingDurationRef = useRef(SLIDE_DURATION);

    useEffect(() => {
        activeVideoRef.current = videoRefs.current[counter] ?? null;
    }, [counter]);

    useEffect(() => {
        const urlsToLoad: string[] = [];
        const currentItem = mediaItems[counter];

        if (currentItem?.mediaType === "video" && currentItem.url) {
            urlsToLoad.push(getVideoUrl(currentItem.url));
        }

        if (mediaItems.length > 1) {
            const nextIndex = (counter + 1) % mediaItems.length;
            const nextItem = mediaItems[nextIndex];

            if (nextItem?.mediaType === "video" && nextItem.url) {
                urlsToLoad.push(getVideoUrl(nextItem.url));
            }
        }

        if (urlsToLoad.length === 0) {return}

        setLoadedVideoUrls(previous => {
            const next = new Set(previous);
            let changed = false;

            urlsToLoad.forEach(url => {
                if (!next.has(url)) {
                    next.add(url);
                    changed = true;
                }
            });

            return changed ? next : previous;
        });
    }, [counter, mediaItems, token]);

    useEffect(() => {
        const activeVideo = videoRefs.current[counter] ?? null;
        activeVideoRef.current = activeVideo;
        
        videoRefs.current.forEach(
            (video, index) => {
                if (!video) { return }
                if (index !== counter) {
                    video.pause();
                }
            }
        );

        if (isPaused || !videoAutoPlayEnabled || mediaItems[counter]?.mediaType !== "video") {
            activeVideo?.pause();
            return;
        }

        if (activeVideo) {
            pauseOtherMedia();
            activeVideo
            .play()
            .catch(() => {
                console.error("Browser may have blocked autoplay");
            });
        }
    }, [counter, isPaused]);
    
    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!containerRef.current) return;
                if (entry.isIntersecting) {
                    setIsPaused(false);
                } else {
                    setIsPaused(true);
                }
            },
            {
                //this is how much of the video needs to be in the display before we play it - so like 100% is ideal
                threshold: 1
            }
        );

        observer.observe(containerRef.current);
        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if (isPaused) {
            if (timerStartedAtRef.current !== null) {
                const elapsed = Date.now() - timerStartedAtRef.current;
                remainingDurationRef.current = Math.max(0, remainingDurationRef.current - elapsed);
            }

            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            timerStartedAtRef.current = null;
            return;
        }

        timerStartedAtRef.current = Date.now();

        timerRef.current = window.setTimeout(() => {
            setCounter(previous => (previous + 1) % mediaItems.length);
            remainingDurationRef.current = SLIDE_DURATION;
            timerStartedAtRef.current = null;
            timerRef.current = null;
        }, remainingDurationRef.current);

        return () => {
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isPaused, counter, mediaItems.length]);

    function setVideoRef(index: number, video: HTMLVideoElement | null) {
        videoRefs.current[index] = video;

        if (index === counter) {
            activeVideoRef.current = video;
        }
    }

    function onPlayClick() {
        setIsPaused(previous => !previous);
    }

    function onNavigateClick(direction: "previous" | "next") {
        remainingDurationRef.current = SLIDE_DURATION;

        //forward and backwards navigate
        setCounter(previous => {
            if (direction === "previous") {
                return previous === 0 ? mediaItems.length - 1 : previous - 1;
            }

            return previous === mediaItems.length - 1 ? 0: previous + 1;
        });
    }

    //builds the videoUrl based on whether tokens are used or not (needed for the Webapp)
    function getVideoUrl(url: string): string {
        return token ? `${url}?access_token=${token}` : url;
    }

    return {containerRef, counter, getVideoUrl, loadedVideoUrls, setVideoRef, isPaused, onPlayClick, onNavigateClick, FADE_DURATION, SLIDE_DURATION}
};


export default UseContentCarousel;